import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiRequestHandler } from "../../apiConfig/service";
import { toast } from "react-toastify";

const VerifyOtp = () => {
    const navigate = useNavigate();
    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const [timer, setTimer] = useState(120); // 2 minutes
    const [canResend, setCanResend] = useState(false);
    const inputRefs = useRef([]);
    const TIMER_DURATION = 120;
    const TIMER_KEY = "otpTimerEnd";

    // Initialize timer
    useEffect(() => {
        const savedEndTime = localStorage.getItem(TIMER_KEY);
        const now = Math.floor(Date.now() / 1000);
        if (savedEndTime) {
            const endTime = parseInt(savedEndTime, 10);
            const remaining = Math.max(0, endTime - now);
            setTimer(remaining);
            setCanResend(remaining <= 0);
        } else {
            const endTime = now + TIMER_DURATION;
            localStorage.setItem(TIMER_KEY, endTime.toString());
            setTimer(TIMER_DURATION);
        }
    }, []);

    // Timer countdown effect
    useEffect(() => {
        if (timer <= 0) {
            setCanResend(true);
            return;
        }

        const interval = setInterval(() => {
            const now = Math.floor(Date.now() / 1000);
            const endTime = parseInt(localStorage.getItem(TIMER_KEY), 10);
            const remaining = Math.max(0, endTime - now);

            setTimer(remaining);
            if (remaining <= 0) {
                setCanResend(true);
                clearInterval(interval);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [timer]);

    // Format MM:SS
    const formatTime = (sec) => {
        const m = Math.floor(sec / 60);
        const s = sec % 60;
        return `${m}:${s < 10 ? "0" : ""}${s}`;
    };

    // Input change & navigation
    const handleChange = (index, value) => {
        if (/^[0-9]$/.test(value) || value === "") {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);

            if (value && index < 5) {
                inputRefs.current[index + 1]?.focus();
            }
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    // Verify OTP
    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        const otpCode = otp.join("");
        if (otpCode.length !== 6) {
            toast.error("Please enter a valid 6-digit OTP");
            return;
        }

        const email = localStorage.getItem("emailForOtp");
        const otpSource = localStorage.getItem("otpSource");

        if (!email || !otpSource) {
            toast.error("Session expired. Please start again.");
            navigate("/user-login");
            return;
        }

        const res = await apiRequestHandler({
            method: "POST",
            endPoint: "verifyOtp2",
            data: { email, otp: otpCode, otpSource },
        });

        if (res?.success) {
            toast.success(res?.message);
            localStorage.removeItem(TIMER_KEY);
            localStorage.removeItem("otpSource");


            navigate("/admin-reset-password");

        } else {
            toast.error(res?.message || "OTP verification failed");
        }
    };

    // Resend OTP
    const handleResendOtp = async (e) => {
        e.preventDefault();
        const email = localStorage.getItem("emailForOtp");
        const otpSource = localStorage.getItem("otpSource");

        if (!email || !otpSource) {
            toast.error("Session expired. Please try again.");
            navigate("/forgot-password");
            return;
        }

        const res = await apiRequestHandler({
            method: "POST",
            endPoint: "forgotPassword",
            data: { email },
        });

        if (res?.success) {
            toast.success("OTP resent successfully");
            const now = Math.floor(Date.now() / 1000);
            const endTime = now + TIMER_DURATION;
            localStorage.setItem(TIMER_KEY, endTime.toString());
            setTimer(TIMER_DURATION);
            setCanResend(false);
            setOtp(["", "", "", "", "", ""]);
            inputRefs.current[0]?.focus();
        } else {
            toast.error(res?.message || "Failed to resend OTP");
        }
    };

    // Focus first input
    useEffect(() => {
        inputRefs.current[0]?.focus();
    }, []);

    return (
        <section className="vh-100 bg-light">
            <div className="container h-100">
                <div className="row d-flex align-items-center justify-content-center h-100">
                    <div className="col-md-8 col-lg-6 col-xl-5">
                        <img
                            src="https://mdbcdn.b-cdn.net/img/Photos/new-templates/bootstrap-login-form/draw2.webp"
                            className="img-fluid"
                            alt="OTP Visual"
                        />
                    </div>
                    <div className="col-md-7 col-lg-5 col-xl-5 offset-xl-1 shadow p-5 bg-white rounded-4">
                        <h3 className="text-center mb-4 fw-bold text-primary">Verify OTP</h3>
                        <form onSubmit={handleVerifyOtp}>
                            <div className="d-flex justify-content-center mb-4">
                                {otp.map((digit, i) => (
                                    <input
                                        key={i}
                                        type="tel"
                                        maxLength="1"
                                        value={digit}
                                        autoComplete="one-time-code"
                                        onChange={(e) => handleChange(i, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(i, e)}
                                        ref={(el) => (inputRefs.current[i] = el)}
                                        className="form-control mx-1 text-center"
                                        style={{ width: "66px", height: "66px", fontSize: "18px" }}
                                    />
                                ))}
                            </div>

                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <small className="text-muted">Check your email for a 6-digit OTP</small>
                                {canResend ? (
                                    <h6
                                        style={{ fontSize: "14px", cursor:"pointer", textDecoration:"underline" }}
                                        onClick={handleResendOtp}
                                    >
                                        Resend OTP
                                    </h6>
                                ) : (
                                    <small className="text-muted">Resend in {formatTime(timer)}</small>
                                )}
                            </div>

                            <button type="submit"                                 className="btn w-100 circle  animation">
                                Verify OTP
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default VerifyOtp;
